import { useState, useEffect } from 'react'
import _ from 'lodash'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useMultiCropSuccessionData } from '~/crop/crop-schedule/hooks/use-multi-crop-succession-data'
import { parseDuration } from '~/crop/crop-schedule/utils/parse-duration'
import { supabaseGetter } from '~/api/supabase-getter'

const isPhaseActiveNow = (phase) => {
  const startStr = phase.started_at || phase.est_started_at
  const durationStr = phase.duration || phase.est_duration
  if (!startStr || !durationStr) return false
  const now = new Date()
  const start = new Date(startStr)
  const days = parseDuration(durationStr)
  const end = new Date(start)
  end.setDate(end.getDate() + days)
  return now >= start && now < end
}

export const useCropPhaseData = () => {
  const { crops, loading: cropsLoading } = useCropData()
  const { successionMap, loading: successionLoading } = useMultiCropSuccessionData({ crops })
  const [phaseData, setPhaseData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (cropsLoading || successionLoading) return
    if (!crops.length) {
      setPhaseData([])
      setLoading(false)
      return
    }

    const parentIds = crops.map(c => c.id)
    const successionIds = [...successionMap.values()].flatMap(s => s.children.map(c => c.id))
    const allCropIds = [...new Set([...parentIds, ...successionIds])]

    // Map every crop ID (including succession children) to its plant count
    const countById = _.mapValues(_.keyBy(crops, 'id'), c => c.count || 1)
    for (const [parentId, { children }] of successionMap.entries()) {
      const parentCount = countById[parentId] || 1
      for (const child of children) {
        countById[child.id] = parentCount
      }
    }

    async function fetchPhases() {
      try {
        const { data: schedules } = await supabaseGetter.cropSchedules.getActiveByCropIds(allCropIds)

        const phaseMap = {}
        for (const schedule of (schedules || [])) {
          const activePhase = schedule.crop_phases?.find(isPhaseActiveNow)
          if (activePhase) phaseMap[schedule.crop_id] = activePhase
        }

        const groups = _.groupBy(
          Object.entries(phaseMap),
          ([, phase]) => phase.phase_type_code
        )
        const result = Object.entries(groups).map(([phaseCode, entries]) => {
          const plantCount = entries.reduce(
            (sum, [cropId]) => sum + (countById[cropId] || 1),
            0
          )
          const phaseType = entries[0][1].phase_type
          return {
            phaseCode,
            label: phaseType?.name || phaseCode,
            cropCount: entries.length,
            plantCount,
          }
        })

        setPhaseData(result)
      } catch {
        setPhaseData([])
      } finally {
        setLoading(false)
      }
    }

    fetchPhases()
  }, [crops, cropsLoading, successionMap, successionLoading])

  return { phaseData: phaseData || [], loading }
}
